import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { exportArrayToCsv } from '@/lib/csvExport';
import { joApi, joErr, sendToText } from '@/services/processingJoApi';
import { OverdueBadge, fmtDay, fmtN } from './JoBits';

const COLS = [['ordered_pcs', 'Ordered'], ['balance_to_send', 'To send'], ['live_slip_pcs', 'On pick slips'], ['at_processing_pcs', 'At Processing'],
  ['with_jobber_pcs', 'With jobber'], ['received_pcs', 'Received'], ['pending_pcs', 'Pending'], ['stock_cover_pcs', 'Greige cover'], ['stock_short_pcs', 'Stock short']];
const TONE = { pending_pcs: 'font-semibold text-gray-900', stock_short_pcs: 'font-semibold text-rose-700', stock_cover_pcs: 'text-emerald-700', with_jobber_pcs: 'text-rose-700' };

const Chip = ({ label, value, tone = 'text-gray-900', testId }) => (
  <div className="rounded-lg border border-[#E5DFD0] bg-white px-3 py-2"><div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-xl font-bold tabular-nums ${tone}`} data-testid={testId}>{value}</div></div>
);

const csv = (rows) => exportArrayToCsv(rows, [{ key: 'jo_id', header: 'Job order' }, { key: 'fg_name', header: 'FG Name' }, { key: 'design_name', header: 'Design' },
  { key: sendToText, header: 'Send to' }, { key: 'target_date', header: 'Expected delivery' }, { key: 'days_late', header: 'Days late' },
  ...COLS.map(([key, header]) => ({ key, header }))], 'processing-job-orders-pending-stock.csv');

/** Open job orders — where the pieces are and whether Greige Storage stock covers the balance. */
export const PendingStockReport = ({ q }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [overdue, setOverdue] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => joApi.pendingReport({ q: q || undefined, overdue: overdue || undefined }).then(setData)
      .catch((e) => toast.error(joErr(e, 'Could not load the report'))), 250);
    return () => clearTimeout(t);
  }, [q, overdue]);
  if (!data) return <Loader2 className="w-6 h-6 animate-spin mx-auto my-12 text-[#2D5F4F]" />;
  const t = data.totals;
  return (
    <div className="space-y-4" data-testid="jo-pending-report">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <Chip label="Open orders" value={fmtN(data.rows.length)} testId="jo-pending-count" />
        <Chip label="Overdue" value={fmtN(data.overdue_count)} tone="text-rose-700" testId="jo-pending-overdue" />
        <Chip label="Pieces pending" value={fmtN(t.pending_pcs)} testId="jo-pending-total" />
        <Chip label="Still to send" value={fmtN(t.balance_to_send)} tone="text-amber-700" testId="jo-pending-to-send" />
        <Chip label="Greige stock short" value={fmtN(t.stock_short_pcs)} tone="text-rose-700" testId="jo-pending-short" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-700"><Switch checked={overdue} onCheckedChange={setOverdue} data-testid="jo-pending-overdue-only" />Overdue only</label>
        <p className="text-xs text-gray-500">Greige cover shares the free stock of each FG over its orders, oldest order first.</p>
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => csv(data.rows)} disabled={!data.rows.length} data-testid="jo-pending-csv-btn"><Download className="w-4 h-4 mr-1" />CSV</Button>
      </div>
      <Card className="border-0 shadow-md"><CardContent className="p-0 overflow-x-auto">
        <Table data-testid="jo-pending-table">
          <TableHeader><TableRow><TableHead>Job order</TableHead><TableHead>FG Name</TableHead><TableHead>Send to</TableHead>
            {COLS.map(([k, l]) => <TableHead key={k} className="text-right whitespace-nowrap">{l}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {!data.rows.length && <TableRow><TableCell colSpan={COLS.length + 3} className="text-center py-10 text-gray-500" data-testid="jo-pending-empty">No open job orders</TableCell></TableRow>}
            {data.rows.map((r) => (
              <TableRow key={r.jo_id} onClick={() => navigate(`/sections/processing/job-orders/${r.jo_id}`)} className={`cursor-pointer hover:bg-[#F5EFE3] ${r.overdue ? 'bg-rose-50/60' : ''}`} data-testid={`jo-pending-row-${r.jo_id}`}>
                <TableCell><div className="font-mono text-sm whitespace-nowrap">{r.jo_id}</div><div className="text-[11px] text-gray-500 whitespace-nowrap">{r.target_date ? `due ${fmtDay(r.target_date)}` : 'no due date'}</div><OverdueBadge days={r.days_late} testId={`jo-pending-overdue-${r.jo_id}`} /></TableCell>
                <TableCell className="max-w-[200px]"><div className="font-mono text-sm truncate">{r.fg_name}</div><div className="text-[11px] text-gray-500 truncate">{r.design_name}</div></TableCell>
                <TableCell className="text-sm max-w-[160px] truncate">{sendToText(r)}</TableCell>
                {COLS.map(([k]) => <TableCell key={k} className={`text-right tabular-nums ${TONE[k] || ''}`} data-testid={`jo-pending-${k}-${r.jo_id}`}>{fmtN(r[k])}</TableCell>)}
              </TableRow>
            ))}
            {data.rows.length > 0 && <TableRow className="bg-[#F5EFE3] font-semibold" data-testid="jo-pending-totals"><TableCell colSpan={3}>Total</TableCell>
              {COLS.map(([k]) => <TableCell key={k} className="text-right tabular-nums" data-testid={`jo-pending-total-${k}`}>{fmtN(t[k])}</TableCell>)}</TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
};

export default PendingStockReport;
